import { AlertOctagon, AlertTriangle, Info, Lock, PowerOff, RotateCcw } from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { Input } from "@/components/atoms/studio/ui/input";
import { toneBadgeVariant } from "@/lib/migrations/coverage";
import {
  RECOMMISSION_MANUAL_STEP,
  decommissionAdvice,
  decommissionBlockers,
  decommissionSource,
  derivedRecommissionToken,
  phasePresentation,
  recommissionBlockers,
} from "@/lib/migrations/decommission";
import { familyPresentation } from "@/lib/migrations/families";
import type {
  EngineDecommissionResult,
  EngineTaskReport,
} from "@/services/migrations/engine";

/**
 * THE DECOMMISSION PHASE, for one task. Switching OFF the automation this migration
 * replaced.
 *
 * WHY A MIGRATION IS NOT FINISHED WHEN THE FLOW IS DEPLOYED, which is the thing this
 * panel exists to say. `apply` creates the replacement and stops, and everything it
 * refuses to do is correct: it deploys the Flow as Draft, it never activates it, and it
 * will not touch the source. But a customer whose WorkflowRule is still firing and whose
 * Flow has been activated has TWO automations on one object — two field updates, two
 * emails, two records. `applied` reads like a finished job, and the entire hazard of this
 * phase is that it is not one. So the PHASE leads this panel, not the status.
 *
 * FOUR PROPERTIES ARE STRUCTURAL HERE, NOT STYLISTIC.
 *
 *  1. THE DOUBLE-FIRE RISK IS THE FIRST THING ON THE PANEL, in the destructive tone, never
 *     behind a disclosure and never below the controls. It is the reason the phase exists.
 *
 *  2. THE TOKEN NAMES THE SOURCE. Every other confirmation in this engine names the
 *     artifact we compiled; this one reads `DEACTIVATE WorkflowRule Account.Set_Rating IN
 *     <org> <compile_key>`, because the deploy behind this gate modifies the CUSTOMER'S
 *     component. It is rendered from `decommission.confirmation_token` — the server's own
 *     string — so what a screen shows and what the gate recomputes cannot drift.
 *
 *  3. THE FLOW MUST BE ACTIVE IN THE ORG, AND WE DO NOT DO THAT. The engine deploys Draft
 *     and never activates: activation is the moment the customer's behaviour changes, and
 *     automating it in the same motion as the deactivation would mean nobody ever saw the
 *     replacement run. So the server refuses `REPLACEMENT_NOT_ACTIVE` until a human has
 *     activated it in Setup, and this panel says so BEFORE the refusal rather than leaving
 *     a reader staring at one.
 *
 *  4. THE ROUTE MAY NOT EXIST. `PLAYGROUND_MIGRATION_DECOMMISSION` is a third flag, default
 *     OFF, and with it off the two routes are not mounted at all — the useful configuration,
 *     and the one every first customer is in for weeks, is apply ON and this OFF. The panel
 *     handles that from `decommission.enabled` and its container handles a 404 the same way
 *     `isEngineAbsent` does.
 *
 * PURE: props in, markup out, no hooks and no queries — which is what lets its tests render
 * the real component and assert on real attributes rather than on a view-model a component
 * is free to ignore.
 */
export function EngineDecommissionPanel({
  task,
  family,
  orgId,
  typedDecommission,
  typedRecommission,
  onTypedDecommission,
  onTypedRecommission,
  onDecommission,
  onRecommission,
  pending = false,
  routeAbsent = false,
  result,
  requestError,
}: {
  task: EngineTaskReport;
  /**
   * The PLAN's family. A task report carries no family of its own, and this panel needs it
   * for exactly one thing: saying what a family with no decommission path leaves running.
   */
  family: string;
  orgId?: string | null;
  typedDecommission: string;
  typedRecommission: string;
  onTypedDecommission?: (value: string) => void;
  onTypedRecommission?: (value: string) => void;
  onDecommission?: () => void;
  onRecommission?: () => void;
  pending?: boolean;
  /** The routes 404'd — the flag flipped off between the report and the click. */
  routeAbsent?: boolean;
  result?: EngineDecommissionResult | null;
  requestError?: string | null;
}) {
  const dc = task.decommission;
  const source = decommissionSource(task);
  const phase = phasePresentation(dc.phase, dc.double_fire_risk);
  const advice = decommissionAdvice(task);

  // A family with no source automation to switch off says so once and renders nothing
  // else. There is no gate here to explain and no control to disable.
  if (!dc.applicable) {
    return <NotApplicable family={family} />;
  }

  const recommissionToken = derivedRecommissionToken(orgId ?? "", source, task.compile_key);
  const forwardBlockers = decommissionBlockers(task, typedDecommission);
  const reverseBlockers = recommissionBlockers(task, typedRecommission, recommissionToken);
  const forwardDisabled = forwardBlockers.length > 0 || pending || routeAbsent;
  const reverseDisabled = reverseBlockers.length > 0 || pending || routeAbsent;

  return (
    <div className="space-y-4">
      {/* ── the risk, first and unconditionally ────────────────────────────── */}
      {advice.map((item) => (
        <AdviceBanner key={item.code} tone={item.tone} message={item.message} />
      ))}

      {/* ── where this migration actually is ───────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
          phase
        </span>
        <Badge variant={toneBadgeVariant(phase.tone)}>{phase.label}</Badge>
        {dc.source_live === true ? (
          <Badge variant="warm" title="The customer's original automation is still running.">
            source live
          </Badge>
        ) : dc.source_live === false ? (
          <Badge variant="secondary">source off</Badge>
        ) : (
          // Tri-state. "We will not say" is not "it is off".
          <Badge variant="secondary">source state not established</Badge>
        )}
      </div>

      {dc.message ? (
        <p className="max-w-prose text-xs text-muted-foreground">{dc.message}</p>
      ) : null}

      {/* ── what would be switched off: THE CUSTOMER'S component ───────────── */}
      <WillDeactivate task={task} />

      {routeAbsent ? (
        <p className="max-w-prose rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
          The decommission routes are not available in this environment. Nothing is broken and
          nothing is missing — this deployment mounts the compile and write paths and not this
          one, which is the intended configuration while a replacement is being watched.
        </p>
      ) : null}

      {/* ── deactivate ─────────────────────────────────────────────────────── */}
      <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <PowerOff className="h-3.5 w-3.5 shrink-0 text-destructive" />
          <span className="font-mono text-[11px] uppercase tracking-wide text-destructive">
            deactivate the source
          </span>
          {dc.approved ? (
            <Badge variant={dc.approval_matches_artifact ? "secondary" : "destructive"}>
              {dc.approval_matches_artifact ? "decommission approved" : "approval stale"}
            </Badge>
          ) : (
            <Badge variant="secondary">not approved</Badge>
          )}
        </div>

        {dc.confirmation_token ? (
          <>
            <p className="max-w-prose text-xs text-muted-foreground">
              To switch this automation off, type the sentence below exactly. It names{" "}
              <span className="font-semibold text-foreground">the source rule</span> — the
              component your org already had — and not the Flow this engine compiled, because
              that is what this deploy modifies.
            </p>
            <p
              className="break-all rounded border border-border bg-muted/30 p-2 font-mono text-[11px] text-foreground"
              data-testid="decommission-token"
            >
              {dc.confirmation_token}
            </p>
            <Input
              value={typedDecommission}
              onChange={(event) => onTypedDecommission?.(event.target.value)}
              placeholder="Type the DEACTIVATE sentence"
              disabled={pending}
              className="max-w-2xl font-mono text-xs"
              spellCheck={false}
              autoComplete="off"
            />
          </>
        ) : null}

        <BlockerList blockers={forwardBlockers} />

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onDecommission}
            disabled={forwardDisabled}
            aria-disabled={forwardDisabled}
            data-testid="decommission-run"
          >
            <PowerOff className="mr-1 h-3.5 w-3.5" />
            {pending ? "Deactivating…" : "Deactivate the source rule"}
          </Button>
          <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
            this stops automation that is running in a real org
          </span>
        </div>
      </div>

      {/* ── reactivate ─────────────────────────────────────────────────────── */}
      <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <RotateCcw className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
            switch it back on
          </span>
        </div>
        <p className="max-w-prose text-xs text-muted-foreground">
          Deactivation destroys nothing — the rule, its criteria, its formula, its actions and
          its time triggers all stay in the org, and this sets one boolean back. What it does
          not undo is the interval: records that changed while the rule was off did not fire
          it. {RECOMMISSION_MANUAL_STEP}
        </p>
        {recommissionToken ? (
          <>
            <p className="max-w-prose text-[11px] text-muted-foreground">
              {/* Said out loud. The report issues the DEACTIVATE token and no other, so this
                  one is computed here; if the derivation is wrong the server refuses with
                  CONFIRMATION_MISMATCH and returns the token it wanted. */}
              This sentence is derived by this screen — the report does not issue one for the
              reverse direction.
            </p>
            <p
              className="break-all rounded border border-border bg-muted/30 p-2 font-mono text-[11px] text-foreground"
              data-testid="recommission-token"
            >
              {recommissionToken}
            </p>
            <Input
              value={typedRecommission}
              onChange={(event) => onTypedRecommission?.(event.target.value)}
              placeholder="Type the REACTIVATE sentence"
              disabled={pending}
              className="max-w-2xl font-mono text-xs"
              spellCheck={false}
              autoComplete="off"
            />
          </>
        ) : null}

        <BlockerList blockers={reverseBlockers} />

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRecommission}
          disabled={reverseDisabled}
          aria-disabled={reverseDisabled}
          data-testid="recommission-run"
        >
          <RotateCcw className="mr-1 h-3.5 w-3.5" />
          {pending ? "Reactivating…" : "Reactivate the source rule"}
        </Button>
      </div>

      {requestError ? (
        <p className="max-w-prose font-mono text-xs text-destructive">
          The request itself failed: {requestError}. Nothing can be concluded about the org from
          a request that did not complete — reload the report.
        </p>
      ) : null}

      {result ? <DecommissionResultBanner result={result} /> : null}
    </div>
  );
}

/**
 * A family with no source automation this phase can switch off.
 *
 * Said rather than hidden, because the difference between "nothing to switch off" and
 * "something still running that we will not switch off" is the whole content of the
 * message for a Process Builder migration.
 */
function NotApplicable({ family }: { family: string }) {
  const presentation = familyPresentation(family);
  return (
    <div className="space-y-2">
      <p className="max-w-prose font-mono text-[11px] text-muted-foreground">
        This migration has no source automation that this phase switches off.
      </p>
      {presentation?.sourceKeepsRunning ? (
        <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--accent-warm))]/40 bg-accent-warm-soft p-3 text-xs text-accent-warm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 leading-relaxed">
            {presentation.sourceKeepsRunning}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** The SOURCE, named as the customer's own component and never as the artifact we made. */
function WillDeactivate({ task }: { task: EngineTaskReport }) {
  const { will_deactivate } = task.decommission;
  if (will_deactivate.length === 0) {
    return (
      <p className="font-mono text-xs text-muted-foreground">
        No source component is recorded for this task, so there is nothing this phase can
        switch off.
      </p>
    );
  }
  return (
    <div className="space-y-1">
      <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
        will deactivate
      </span>
      <ul className="space-y-0.5">
        {will_deactivate.map((item) => (
          <li
            key={`${item.metadata_type}-${item.full_name}`}
            className="break-all font-mono text-xs text-foreground"
          >
            {item.metadata_type} · {item.full_name}
          </li>
        ))}
      </ul>
      <p className="max-w-prose text-[11px] text-muted-foreground">
        This is your org&apos;s own component, not one this engine compiled. The deploy sets one
        boolean on it and asserts before sending that exactly one key changed — a WorkflowRule
        deploy is an upsert, so a payload carrying only the flag would overwrite the rule&apos;s
        criteria and actions and still report success.
      </p>
    </div>
  );
}

function AdviceBanner({ tone, message }: { tone: string; message: string }) {
  const destructive = tone === "destructive";
  return (
    <div
      className={[
        "flex items-start gap-2 rounded-md border p-3 text-xs",
        destructive
          ? "border-destructive/40 bg-destructive/10 text-destructive"
          : "border-[hsl(var(--accent-warm))]/40 bg-accent-warm-soft text-accent-warm",
      ].join(" ")}
      data-testid={destructive ? "decommission-risk" : "decommission-advice"}
    >
      {destructive ? (
        <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0" />
      ) : (
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
      )}
      <span className="min-w-0 flex-1 leading-relaxed">{message}</span>
    </div>
  );
}

function BlockerList({ blockers }: { blockers: Array<{ code: string; message: string }> }) {
  if (blockers.length === 0) return null;
  return (
    <ul className="space-y-1">
      {blockers.map((blocker) => (
        <li key={blocker.code} className="flex items-start gap-2 text-xs text-muted-foreground">
          <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1 leading-relaxed">
            <span className="font-mono uppercase">{blocker.code}</span> — {blocker.message}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The outcome. `sent` leads, exactly as it does for an apply, and for the sharper version
 * of the same reason: whether a customer's automation is currently running is the one
 * thing a reader must never have to infer from a status code.
 */
function DecommissionResultBanner({ result }: { result: EngineDecommissionResult }) {
  const refused = !result.sent;
  return (
    <div
      className={[
        "space-y-1 rounded-md border p-3 text-xs",
        refused
          ? "border-border bg-muted/20 text-muted-foreground"
          : "border-[hsl(var(--accent-warm))]/45 bg-accent-warm-soft text-accent-warm",
      ].join(" ")}
      data-testid="decommission-result"
    >
      <p className="font-semibold">
        {refused
          ? "Nothing was sent — this automation was not touched."
          : "This request reached the org."}
      </p>
      <p className="font-mono text-[11px] uppercase">
        {result.outcome}
        {result.code ? ` · ${result.code}` : ""}
      </p>
      {result.source ? (
        <p className="break-all font-mono text-[10px]">source {result.source}</p>
      ) : null}
      {result.message ? <p className="max-w-prose leading-relaxed">{result.message}</p> : null}
      {result.expected ? (
        <p className="break-all font-mono text-[10px]">expected token: {result.expected}</p>
      ) : null}
    </div>
  );
}
